import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  chart: string;
  id?: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart, id }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize mermaid with configuration
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      fontFamily: 'inherit',
      fontSize: 14,
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis'
      },
      sequence: {
        useMaxWidth: true,
        wrap: true
      },
      pie: {
        useMaxWidth: true
      }
    });
  }, []);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!ref.current || !chart.trim()) return;

      setIsLoading(true);
      setError(null);

      try {
        // Generate a unique ID for this diagram
        const diagramId = id || `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        
        // Clear the container
        ref.current.innerHTML = '';
        
        // Parse and render the mermaid diagram
        const { svg } = await mermaid.render(diagramId, chart);
        
        // Insert the SVG into the container
        ref.current.innerHTML = svg;
        
        setIsLoading(false);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
        setIsLoading(false);
      }
    };

    renderDiagram();
  }, [chart, id]);

  if (error) {
    return (
      <div style={{
        padding: '16px',
        border: '1px solid #ff6b6b',
        borderRadius: '4px',
        backgroundColor: '#ffe0e0',
        color: '#d63031',
        margin: '16px 0'
      }}>
        <strong>Diagram Error:</strong> {error}
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer' }}>Show diagram source</summary>
          <pre style={{ 
            marginTop: '8px', 
            padding: '8px', 
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            fontSize: '12px',
            overflow: 'auto'
          }}>
            {chart}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div style={{ margin: '16px 0', textAlign: 'center' }}>
      {isLoading && (
        <div style={{
          padding: '32px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Rendering diagram...
        </div>
      )}
      <div
        ref={ref}
        style={{
          display: isLoading ? 'none' : 'block',
          maxWidth: '100%',
          overflow: 'auto'
        }}
      />
    </div>
  );
};

export default MermaidDiagram;
